import {WeddingLookbookFilter} from "./WeddingLookbookFilter.js"

/**
 * @typedef LookbookDealObj
 * @property {number} id
 * @property {string} eventState
 * @property {string} styleProfile
 * @property {string} colorPalette
 * @property {string} season
 */

/**
 * @typedef LookbookPhotoObj
 * @property {number} id
 * @property {string} url
 * @property {string} filepath
 * @property {number} size
 * @property {number} height
 * @property {number} width
 * @property {string} category
 * @property {string} photographerName
 * @property {string} photographerSocial
 * @property {string} photographerWebsite
 * @property {number} DealId
 * @property {string} eventState
 * @property {string} styleProfile
 * @property {string} colorPalette
 * @property {string} season
 */

class WeddingPhotoModal {
  constructor() {
    /** @type {HTMLElement} */
    this.photoModalEl = null
    /** @type {HTMLImageElement} */
    this.photoModalImgEl = null
    /** @type {HTMLParagraphElement} */
    this.photoModalTitleEl = null
    /** @type {HTMLButtonElement} */
    this.nextPhotoBtnEl = null
    /** @type {HTMLButtonElement} */
    this.prevPhotoBtnEl = null
    /** @type {HTMLAnchorElement} */
    this.photoModalStartBtnEl = null
    /** @type {HTMLAnchorElement} */
    this.photoModalShopBtnEl = null
    /** @type {HTMLParagraphElement} */
    this.plainTextCreditEl = null
    /** @type {HTMLAnchorElement} */
    this.anchorTextCreditEl = null

    this.isOpen = false

    /** @type {LookbookPhotoObj[]} */
    this.filteredPhotos = []
    this.currentPhotoIndex = 0

    this.buildModal()
  }

  get hasNextPhoto() {
    return this.currentPhotoIndex < this.filteredPhotos.length - 1
  }

  get hasPrevPhoto() {
    return this.currentPhotoIndex > 0
  }

  buildModal() {
    this.photoModalEl = document.createElement('div')
    this.photoModalEl.className = 'fixed inset-0 w-screen h-screen bg-black/80 flex items-center justify-center'
    this.photoModalEl.style.zIndex = 100

    const photoModalInnerWrapper = document.createElement('div')
    photoModalInnerWrapper.className = 'flex items-center'

    this.prevPhotoBtnEl = document.createElement('button')
    this.prevPhotoBtnEl.className = 'rounded-full bg-offblack hover:bg-copper h-10 w-10 flex items-center justify-center mx-4'
    this.prevPhotoBtnEl.style.minWidth = '40px'
    this.prevPhotoBtnEl.innerHTML = '<span class="icon-arrow_back text-2xl md-light text-white"></span>'
    photoModalInnerWrapper.appendChild(this.prevPhotoBtnEl)

    const photoImgContainerEl = document.createElement('div')
    photoImgContainerEl.className = 'h-full'

    this.plainTextCreditEl = document.createElement('p')
    this.plainTextCreditEl.className = 'font-barlow font-medium text-sm text-white'
    this.plainTextCreditEl.textContent = ''
    this.plainTextCreditEl.style.display = 'none'
    photoImgContainerEl.appendChild(this.plainTextCreditEl)
  
    this.anchorTextCreditEl = document.createElement('a')
    this.anchorTextCreditEl.className = 'font-barlow font-medium text-sm text-white hover:underline'
    this.anchorTextCreditEl.textContent = ''
    this.anchorTextCreditEl.href = '#'
    this.anchorTextCreditEl.style.display = 'none'
    this.anchorTextCreditEl.addEventListener('click', (e) => {
      e.stopPropagation()
    })
    photoImgContainerEl.appendChild(this.anchorTextCreditEl)

    this.photoModalImgEl = document.createElement('img')
    this.photoModalImgEl.src = ''
    this.photoModalImgEl.style.height = '600px'
    this.photoModalImgEl.className = 'mb-4'
    photoImgContainerEl.appendChild(this.photoModalImgEl)

    this.photoModalTitleEl = document.createElement('p')
    this.photoModalTitleEl.className = 'font-barlow font-medium text-lg md:text-2xl text-white mb-2'
    this.photoModalTitleEl.textContent = ''
    photoImgContainerEl.appendChild(this.photoModalTitleEl)

    const buttonContainerEl = document.createElement('div')
    buttonContainerEl.className = 'flex md:flex-row flex-col md:items-center'

    this.photoModalStartBtnEl = document.createElement('a')
    this.photoModalStartBtnEl.href = ''
    this.photoModalStartBtnEl.style.display = 'flex'
    this.photoModalStartBtnEl.className = 'font-barlow uppercase align-top transition-colors duration-200 ease-in-out tracking-widest sm:tracking-[3px] bg-copper text-white hover:text-white hover:bg-offblack px-4 sm:px-5 justify-center text-base h-10 border border-copper hover:border-offblack items-center'
    this.photoModalStartBtnEl.innerText = 'Start Planning'
    this.photoModalStartBtnEl.addEventListener('click', (e) => {
      e.stopPropagation()
    })
    buttonContainerEl.appendChild(this.photoModalStartBtnEl)

    this.photoModalShopBtnEl = document.createElement('a')
    this.photoModalShopBtnEl.href = ''
    this.photoModalShopBtnEl.style.display = 'flex'
    this.photoModalShopBtnEl.className = 'font-barlow uppercase align-top transition-colors duration-200 ease-in-out tracking-widest sm:tracking-[3px] bg-transparent text-white hover:text-black hover:bg-white px-4 sm:px-5 md:ml-2 justify-center md:mt-0 mt-2 text-base h-10 border border-white items-center'
    this.photoModalShopBtnEl.innerText = 'Shop The Style'
    this.photoModalShopBtnEl.addEventListener('click', (e) => {
      e.stopPropagation()
    })
    buttonContainerEl.appendChild(this.photoModalShopBtnEl)

    photoImgContainerEl.appendChild(buttonContainerEl)

    photoModalInnerWrapper.appendChild(photoImgContainerEl)

    this.nextPhotoBtnEl = document.createElement('button')
    this.nextPhotoBtnEl.className = 'rounded-full bg-offblack hover:bg-copper h-10 w-10 flex items-center justify-center mx-4'
    this.nextPhotoBtnEl.style.minWidth = '40px'
    this.nextPhotoBtnEl.innerHTML = '<span class="icon-arrow_forward text-2xl md-light text-white"></span>'
    photoModalInnerWrapper.appendChild(this.nextPhotoBtnEl)

    this.photoModalEl.appendChild(photoModalInnerWrapper)

    const closeBtnEl = document.createElement('button')
    closeBtnEl.className = 'absolute top-4 right-4 rounded-full bg-white hover:bg-copper h-10 w-10 flex items-center justify-center mx-4'
    closeBtnEl.innerHTML = '<span class="icon-close text-2xl md-light text-black"></span>'
    this.photoModalEl.appendChild(closeBtnEl)

    this.prevPhotoBtnEl.addEventListener('click', this.prevPhoto.bind(this))
    this.nextPhotoBtnEl.addEventListener('click', this.nextPhoto.bind(this))
    photoModalInnerWrapper.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
    })
    this.photoModalEl.addEventListener('click', this.closeModal.bind(this))

    window.addEventListener('keydown', this.keydownEvt.bind(this))
  }

  /**
   * 
   * @param {KeyboardEvent} e 
   */
  keydownEvt(e) {
    if (!this.isOpen) return

    if (e.key === 'ArrowLeft' && this.hasPrevPhoto) {
      this.prevPhoto()
    } else if (e.key === 'ArrowRight' && this.hasNextPhoto) {
      this.nextPhoto()
    } else if (e.key === 'Escape') {
      this.closeModal()
    }
  }

  nextPhoto() {
    this.setPhoto(this.filteredPhotos[this.currentPhotoIndex + 1])
  }

  prevPhoto() {
    this.setPhoto(this.filteredPhotos[this.currentPhotoIndex - 1])
  }

  showHideNextPrevButtons() {
    this.prevPhotoBtnEl.style.display = this.currentPhotoIndex > 0 ? 'block' : 'none'
    this.nextPhotoBtnEl.style.display = this.currentPhotoIndex < this.filteredPhotos.length - 1 ? 'block' : 'none'
  }

  /**
   * 
   * @param {LookbookPhotoObj} photo 
   */
  setPhoto(photo) {
    if (!photo) return
    this.currentPhotoIndex = this.filteredPhotos.findIndex(p => p.id === photo.id)

    let maxWidth = window.innerWidth - 100
    let maxHeight = window.innerHeight - 300

    const aspectRatio = photo.width / photo.height
    let imgHeight = maxHeight
    let imgWidth = maxHeight * aspectRatio
    if (imgWidth > maxWidth) {
      imgHeight = maxWidth / aspectRatio
    }
    this.photoModalImgEl.style.height = imgHeight + 'px'

    // Set image src
    this.photoModalImgEl.src = photo.url

    // Set photo title (style - color)
    if (photo.styleProfile && photo.colorPalette) {
      this.photoModalTitleEl.textContent = `${photo.styleProfile} - ${photo.colorPalette}`
    }

    // Set Start Planning button
    this.photoModalStartBtnEl.href = `https://amxhub.site/your-flowers`
    if (photo.styleProfile) {
      const urlParams = new URLSearchParams()
      urlParams.set('photoid', photo.id)
      urlParams.set('style', photo.styleProfile)
      if (photo.colorPalette) {
        urlParams.set('color', photo.colorPalette)
      }
      if (photo.DealId) {
        urlParams.set('dealid', photo.DealId)
      }
      this.photoModalStartBtnEl.href += `?${urlParams.toString()}`
    }

    // Set Shop Style button
    if (['Avant Garde', 'Lush', 'Seasonal'].includes(photo.styleProfile)) {
      // Show shop button
      this.photoModalShopBtnEl.style.display = 'flex'
      let colorPaletteSlug = photo.colorPalette
      if (colorPaletteSlug === 'Blush Ivory') colorPaletteSlug = 'Blush %26 Ivory'
      else if (colorPaletteSlug === 'White Cream') colorPaletteSlug = 'White %26 Cream'
      else if (colorPaletteSlug === 'Tan Neutral') colorPaletteSlug = 'Tan %26 Neutral'
      this.photoModalShopBtnEl.href = `/collections/self-service-wedding-flowers?filter.p.m.weddings.style=${photo.styleProfile}&filter.p.m.weddings.palette=${colorPaletteSlug}`
    } else {
      this.photoModalShopBtnEl.style.display = 'none'
    }

    this.setPhotoCredit(photo)

    document.body.appendChild(this.photoModalEl)

    this.showHideNextPrevButtons()
  }

  /**
   * 
   * @param {LookbookPhotoObj} photo 
   */
  setPhotoCredit(photo) {
    if (!photo.photographerName) return

    // Clean photographer website url
    let photographerWebsite = photo.photographerWebsite?.trim()
    if (photographerWebsite) {
      if (!photographerWebsite.includes('.')) {
        // Invalid photographer website
        photographerWebsite = null
      } else if (photographerWebsite.includes('@')) { // Treat as email
        photographerWebsite = `mailto:${photographerWebsite}`
      } else if (!photographerWebsite.startsWith('http')) {
        photographerWebsite = `https://${photographerWebsite}`
      }
    }

    if (photographerWebsite) {
      this.anchorTextCreditEl.textContent = photo.photographerName
      this.anchorTextCreditEl.href = photographerWebsite
      this.anchorTextCreditEl.style.display = 'block'
      this.plainTextCreditEl.style.display = 'none'
    } else {
      this.plainTextCreditEl.textContent = photo.photographerName
      this.plainTextCreditEl.style.display = 'block'
      this.anchorTextCreditEl.style.display = 'none'
    }
  }

  /**
   * 
   * @param {LookbookPhotoObj} photo 
   * @param {LookbookPhotoObj[]} filteredPhotos
   */
  openModal(photo, filteredPhotos) {
    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'
    this.filteredPhotos = filteredPhotos
    this.setPhoto(photo)
    this.isOpen = true
  }

  /**
   * 
   * @param {Event} [e] 
   */
  closeModal(e) {
    e?.stopPropagation()
    e?.preventDefault()
    document.body.style.removeProperty('overflow')
    document.documentElement.style.removeProperty('overflow')
    this.filteredPhotos = []
    this.photoModalEl.remove()
    this.isOpen = false
  }
}

class WeddingLookbook {
  constructor() {
    this.lookbookWrapperEl = null
    this.lookbookFiltersWrapperEl = null
    this.lookbookWrapperWidth = 0
    this.loadMoreBtnEl = null

    /** @type {LookbookPhotoObj[]} */
    this.photos = []
    /** @type {LookbookPhotoObj[]} */
    this.filteredPhotos = []
    this.lastIndex = 0
    this.lastRow = 0

    /** @type {WeddingLookbookFilter[]} */
    this.weddingLookbookFilters = []

    /** @type {HTMLElement} */
    this.photoModalEl = null
    /** @type {HTMLImageElement} */
    this.photoModalImgEl = null

    /** @type {WeddingPhotoModal} */
    this.weddingPhotoModal = null

    this.altTexts = [
      "Wedding flowers decorate a bridal archway",
      "Bridal bouquets with white and red roses",
      "Avante Garde colorful bridal bouquet",
      "Lush white cream wedding flowers",
      "Flowers for tables at the wedding reception",
      "Flowers for weddings: mantle display and table flowers",
      "All white bridal bouquet",
      "Lush white cream floral centerpiece",
      "Flowers for weddings:  arch floral design",
      "Lush tan neutral bridal bouquet",
      "Classic blush ivory floral decorations for the reception",
      "Flowers for the wedding ceremony location",
      "Wedding flowers backdrop for the ceremony",
      "Wedding flowers for the bride and her bridesmaids",
      "Avante Garde tan neutral flowers for the wedding reception table"
    ]
  }

  get hasFiltersEnabled() {
    return this.weddingLookbookFilters.some(wlf => wlf.value !== null)
  }

  setFilteredPhotos() {
    this.filteredPhotos = this.photos.filter((photo) => {
      return !this.weddingLookbookFilters.some(wlf => !wlf.checkFilterPhoto(photo))
    })
  }

  showHideLoadMoreBtn() {
    if (!this.loadMoreBtnEl) return
    this.loadMoreBtnEl.style.display = this.lastIndex >= this.filteredPhotos.length ? 'none' : 'inline-block'
  }

  showHideNoPhotosText() {
    const noPhotosText = document.getElementById('noPhotosText')
    if (!noPhotosText) return
    noPhotosText.style.display = (this.filteredPhotos.length || !this.hasFiltersEnabled) ? 'none' : 'block'
  }

  /**
   * Get all lookbook photos
   * 
   * @returns {Promise<LookbookPhotoObj[]>}
   */
  loadLookbookPhotos() {
    return fetch('https://deals.f4d.space/api/lookbooks/photos', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: `application/json`,
        Authorization: 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJkYXRhIjoibG9va2Jvb2siLCJleHAiOjE3MzQxMjI0NjQwNzcsImlhdCI6MTcwMzAxODQ2NH0.PGUNl-GFGR2_EjWBEr9NOUovoJ-RBdZyWYnrmygz8AM'
      },
      mode: 'cors'
    }).then((response) => response.json()).then((data) => {
      return data.photos.map((photo) => {
        /** @type {LookbookDealObj} */
        const photoDeal = { ...photo.Deal }
        photo.DealId = photoDeal.id
        delete photo.Deal
        return {
          ...photoDeal,
          ...photo
        }
      })
    }).catch((error) => {
      console.error('Failed to load lookbook photos', error)
      return []
    })
  }

  /**
   * @param {LookbookPhotoObj} photo
   * @returns {HTMLElement}
   */
  getImageElement(photo, extraWide = false) {
    const imgWrapper = document.createElement('div')
    imgWrapper.style.width = extraWide ? `66.666%` : `33.333%`
    imgWrapper.style.aspectRatio = extraWide ? '2/1' : '1/1'
    imgWrapper.className = 'border-4 border-transparent' // using 4px border instead of 4px padding
    imgWrapper.style.position = 'relative'

    const imgElement = document.createElement('img')
    imgElement.src = photo.url
    imgElement.alt = this.altTexts[this.lastIndex]
    imgElement.style.width = '100%'
    imgElement.style.height = '100%'
    imgElement.style.objectFit = 'cover'
    imgWrapper.appendChild(imgElement)

    const imgOverlay = document.createElement('div')
    imgOverlay.className = 'absolute inset-0 w-full h-full bg-black bg-opacity-60 opacity-0 hover:opacity-100 cursor-pointer'

    if (photo.styleProfile && photo.colorPalette) {
      const imgOverlayNameText = document.createElement('p')
      imgOverlayNameText.className = 'absolute bottom-2 md:bottom-8 left-2 md:left-8 font-barlow font-medium text-base sm:text-lg md:text-2xl text-white'
      imgOverlayNameText.textContent = `${photo.styleProfile} - ${photo.colorPalette}`
      imgOverlay.appendChild(imgOverlayNameText)
    }

    imgWrapper.appendChild(imgOverlay)

    imgOverlay.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      this.openPhotoModal(photo)
    })
    
    return imgWrapper
  }

  /**
   * Shows 6 more rows
   */
  showMoreRows() {
    this.showHideNoPhotosText()
    if (this.lastIndex >= this.filteredPhotos.length) {
      // No more photos
      this.showHideLoadMoreBtn()
      return
    }

    let previousHasLongJohn = false

    let lastRowToFill = this.lastRow + 6
    for (this.lastRow; this.lastRow < lastRowToFill; this.lastRow++) {
      if (this.lastRow % 2 === 0) previousHasLongJohn = false

      let rowWidth = 3
      while(rowWidth > 0 && this.lastIndex < this.filteredPhotos.length) {
        const photo = this.filteredPhotos[this.lastIndex]

        const aspectRatio = photo.width / photo.height
        const isLongBoy = aspectRatio > 1.4 && rowWidth >= 2 && !previousHasLongJohn
        const imgElement = this.getImageElement(photo, isLongBoy)
        this.lookbookWrapperEl.appendChild(imgElement)
        rowWidth -= isLongBoy ? 2 : 1
        if (isLongBoy) previousHasLongJohn = true
        
        this.lastIndex++ 
      }
    }

    this.showHideLoadMoreBtn()
  }

  initFilterDropdowns() {
    const stylePaletteMap = {
      Classic: ['White Cream', 'Blush Ivory', 'Tan Neutral', 'Jewel', 'Colorful'],
      Lush: ['White Cream', 'Blush Ivory', 'Tan Neutral', 'Jewel', 'Colorful'],
      'Avant Garde': ['White Cream', 'Blush Ivory', 'Tan Neutral', 'Jewel', 'Colorful'],
      Seasonal: ['Meadow', 'Garden', 'Orchard', 'Alpine'],
      Tropical: ['Neutral', 'Bright']
    }
    const filters = [
      {
        id: 'season',
        label: 'Season',
        items: ['Show All', 'Fall', 'Spring', 'Summer', 'Winter'],
      },
      {
        id: 'styleProfile',
        label: 'Style',
        items: ['Show All', 'Classic', 'Lush', 'Avant Garde', 'Seasonal', 'Tropical'],
        itemsFilter: () => {
          const selectedPalette = this.weddingLookbookFilters.find(wlf => wlf.filterData.id === 'colorPalette')?.value
          const options = ['Show All']
          for (const style in stylePaletteMap) {
            if (!selectedPalette || stylePaletteMap[style].includes(selectedPalette)) options.push(style)
          }
          return options
        }
      },
      {
        id: 'colorPalette',
        label: 'Palette',
        items: ['Show All', 'White Cream', 'Blush Ivory', 'Tan Neutral', 'Jewel', 'Colorful', 'Meadow', 'Garden', 'Orchard', 'Alpine', 'Neutral', 'Bright'],
        itemsFilter: () => {
          const selectedStyle = this.weddingLookbookFilters.find(wlf => wlf.filterData.id === 'styleProfile')?.value
          if (!selectedStyle) return ['Show All', 'White Cream', 'Blush Ivory', 'Tan Neutral', 'Jewel', 'Colorful', 'Meadow', 'Garden', 'Orchard', 'Alpine', 'Neutral', 'Bright']
          return [
            'Show All',
            ...stylePaletteMap[selectedStyle]
          ]
        }
      },
      {
        id: 'category',
        label: 'Category',
        items: ['Show All', 'Bouquet', 'Ceremony', 'Detail', 'Reception'],
      }
    ]

    filters.forEach((filterData) => {
      const lookbookFilter = new WeddingLookbookFilter(filterData, this.lookbookFiltersWrapperEl)
      this.weddingLookbookFilters.push(lookbookFilter)
    })

    window.addEventListener('click', (e) => {
      this.weddingLookbookFilters.forEach((lookbookFilter) => {
        if (!lookbookFilter.isOpen) return

        if (!lookbookFilter.el.contains(e.target)) {
          lookbookFilter.toggle()
        } else if (e.target.dataset?.filtervalue) {
          if (lookbookFilter.select(e.target.dataset.filtervalue)) {
            this.refreshLookbookFilters()
            this.refreshPhotos()
          }
        }
      })
    })
  }

  refreshLookbookFilters() {
    this.weddingLookbookFilters.forEach((lookbookFilter) => lookbookFilter.refreshFilterItems())
  }

  refreshPhotos() {
    this.setFilteredPhotos()
    this.lookbookWrapperEl.innerHTML = ''
    this.lastIndex = 0
    this.lastRow = 0
    this.showMoreRows()
  }

  /**
   * @param {HTMLElement} el
   * @param {HTMLElement} filtersEl
   */
  async setLookbookPhotos(el, filtersEl) {
    this.lookbookWrapperEl = el
    this.lookbookFiltersWrapperEl = filtersEl
    this.photos = await this.loadLookbookPhotos()
    this.filteredPhotos = this.photos
    if (!this.photos.length) return

    this.initFilterDropdowns()

    this.loadMoreBtnEl = document.getElementById('loadMoreButton')
    if (this.loadMoreBtnEl) {
      this.loadMoreBtnEl.addEventListener('click', this.showMoreRows.bind(this))
    }

    this.lookbookWrapperWidth = el.clientWidth

    this.showMoreRows()
  }

  /**
   * 
   * @param {LookbookPhotoObj} photo 
   */
  openPhotoModal(photo) {
    if (!this.weddingPhotoModal) {
      // First time create modal
      this.weddingPhotoModal = new WeddingPhotoModal()
    }

    this.weddingPhotoModal.openModal(photo, this.filteredPhotos)
  }

  closePhotoModal() {
    this.weddingPhotoModal?.closeModal()
  }
}

const weddingLookbook = new WeddingLookbook()

document.addEventListener('DOMContentLoaded', function () {
  const el = document.getElementById('lookbook-wrapper')
  const filtersWrapper = document.getElementById('lookbook-filters-wrapper')
  weddingLookbook.setLookbookPhotos(el, filtersWrapper)
})